import { CommonModule } from '@angular/common';
import { Component, Input, NgModule, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';

import { TranslateService } from '@codeandweb/ngx-translate';

import { Subscription } from 'rxjs';

import { StateService, UIRouterModule } from '@uirouter/angular';

import { MenuModule } from '@app/primeng-overrides/menu';
import { Table, TableModule } from '@app/primeng-overrides/table';
import { ConfirmationService, LazyLoadEvent, MenuItem } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { ToolbarModule } from 'primeng/toolbar';

import { AbonneLogoDisplayModule } from '@app/abonne/abonne-logo-display/abonne-logo-display';
import { Abonne } from '@app/abonne/abonne.model';
import { AbonneService } from '@app/abonne/abonne.service';
import { Utilisateur } from '@app/utilisateur/utilisateur.model';
import { GlobalModule } from '@global/global.module';
import { StorageService } from '@global/storage.service';

import { EventManagerService } from '@app/_global/event-manager.service';
import { createDownloadFromHttpResponse } from '@app/_helpers/utils';
import { environment } from '@environments/environment';



@Component({
	selector: 'liste-abonnes',
	templateUrl: `liste-abonnes.component.html`
})
export class ListeAbonnesComponent implements OnInit, OnDestroy {

	@ViewChild('tableAbonnes', { static: true }) table: Table;
	@Input() me: Utilisateur;
	now = Date.now();

	values: Abonne[] = [];
	totalRecords: number = 0;
	loading: boolean;
	rows: number = environment.default_rows;

	params: { [key: string]: any } = {
		search: null,
		abo_actif: true
	};

	menuItems: MenuItem[] = [];
	canEdit = false;

	subscriptionLangChange: Subscription;

	constructor(
		private storageService: StorageService,
		private stateService: StateService,
		private abonneService: AbonneService,
		private translate: TranslateService,
		private confirmationService: ConfirmationService,
		private eventManager: EventManagerService,
	) {
		this.subscriptionLangChange = this.translate.onLangChange.subscribe(() => {
			this.initializeMenu();
		});
	}

	ngOnInit(): void {
		this.canEdit = this.me.uti_administrateur;
		this.initializeMenu();
		this.getParamSet();
	}

	ngOnDestroy(): void {
		this.subscriptionLangChange.unsubscribe();
	}

	private initializeMenu() {
		if (this.canEdit) {
			this.menuItems = [
				{
					label: this.translate.instant('common.create_action_label'),
					icon: 'pi pi-plus',
					command: () => {
						this.stateService.go('.create');
					}
				}
			];
		}

		this.menuItems.push({
			label: this.translate.instant('common.export_action_label'),
			icon: 'pi pi-download',
			command: () => {
				this.exportAbonneList();
			}
		});
	}



	getParamSet() {
		let tableParams = this.storageService.getForCurrentState('tableParams', undefined, true);
		if (tableParams) {
			for (let prop in this.params) {
				if (typeof tableParams[prop] != 'undefined') this.params[prop] = tableParams[prop];
			}
			if (tableParams.first) this.table.first = tableParams.first;
			if (tableParams.rows) this.rows = tableParams.rows;
		}
		this.table.rows = this.rows;
	}

	saveParamSet() {
		if (this.table) {
			let tableParams = Object.assign({}, this.params, {
				first: this.table.first,
				rows: this.table.rows
			});
			this.storageService.setForCurrentState('tableParams', tableParams, true);

			const uiParams: any = Object.assign({}, this.stateService.params, this.params, {
				abo_actif: undefined
			});
			this.stateService.go('.', uiParams, { reload: false, location: 'replace', inherit: false });
		}
	}

	resetSearch() {
		this.params.search = null;
	}

	refresh(stayOnPage?: boolean) {
		if (!stayOnPage) this.table._first = 0;
		this.table.onLazyLoad.emit(this.table.createLazyLoadMetadata());
	}

	loadAbonnes(event: LazyLoadEvent) {
		// https://github.com/primefaces/primeng/issues/8465#issuecomment-617887919
		Promise.resolve(null).then(() => {
			this.loading = true;
		});

		const tmpParams = Object.assign({}, event, this.params);

		this.saveParamSet();

		this.abonneService
			.getAbonnes(tmpParams)
			.subscribe({
				next: ({ abonnes, total }) => {
					this.totalRecords = total;
					this.values = abonnes;

					// si on revient sur la liste paginée et que la page en question est vide, on reset
					if (this.values.length === 0 && this.table.first != 0) {
						this.refresh();
					}
				}
			})
			.add(() => {
				this.loading = false;
			});
	}

	exportAbonneList(){
		this.abonneService.exportAbonnes(this.params)
		.subscribe(response => {
			createDownloadFromHttpResponse(response);
		});
	}

	deleteAbonne(abonne: Abonne) {
		let message = this.translate.instant('abonnes.deletion_confirmation', {name: `<b>${abonne.abo_nom_abonne}</b>`});

		this.confirmationService.confirm({
			defaultFocus: 'reject',
			message: message,
			accept: () => {
				this.abonneService.deleteAbonne(abonne.abo_id).subscribe({
					complete: () => {
						this.eventManager.emit('toast', { severity: 'success', summary: this.translate.instant('abonnes.deletion_success') });
						this.refresh(true);
					}
				});
			}
		});
	}

	formatFamilyList(abonne: Abonne){
		return abonne.familles.map(famille => famille.fam_nom).join(', ');
	}
}

@NgModule({
	imports: [
		CommonModule,
		GlobalModule,
		UIRouterModule,
		FormsModule,
		ButtonModule,
		TableModule,
		ToolbarModule,
		MenuModule,
		AbonneLogoDisplayModule
	],
	exports: [ListeAbonnesComponent],
	declarations: [ListeAbonnesComponent]
})
export class ListeAbonnesModule { }
